import React, { useState } from "react";

type ButtonSize = 'lg' | 'md' | 'sm';
type ButtonVariant = 'default' | 'outline' | 'gray' | 'text' | 'danger' | 'danger_text';
type ButtonWidth = 'fit' | 'full';

interface ComboButton {
  name: string;
  onClick: () => void;
}

interface CustomButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  size?: ButtonSize;
  variant?: ButtonVariant;
  disabled?: boolean;
  width?: ButtonWidth;
  customStyle?: string;
  error?: boolean;
  isCombo?: boolean;
  comboButtons?: ComboButton[];
}

const CustomButton = ({
  size = "lg",
  variant = "default",
  disabled = false,
  width = "fit",
  customStyle = "",
  isCombo = false,
  comboButtons = [],
  ...props
}: CustomButtonProps) => {
  const noSizeVariants = ['text', 'danger_text'] as const;
  const [isOpen, setIsOpen] = useState(false);
  const styleBasedSizeProp: Record<ButtonSize, string> = {
    lg: "py-3 px-6 text-base leading-6 h-12",
    md: "px-4 py-[0.5625rem] text-sm leading-5 h-9",
    sm: "px-2 py-[0.3125rem] text-xs leading-[1.125rem] h-[1.75rem]",
  };

  const styleComboBasedSizeProp: Record<ButtonSize, string> = {
    lg: "py-3 px-6 text-base leading-6 h-12",
    md: "pl-4 pr-2 py-[0.5625rem] text-sm leading-5 h-9",
    sm: "pl-3 pr-[0.375rem] py-2 text-xs leading-[1.125rem] h-[1.75rem]",
  };

  const styleBasedVariantProp: Record<ButtonVariant, string> = {
    default:
      `text-white font-bold bg-[#FF861A] border border-[#FF861A] ${
      !disabled ? "hover:border-transparent hover:bg-[#EB6E01]" : ""
    } focus:border-transparent focus:bg-[#EB6E01] disabled:bg-[#BBB] disabled:border-transparent`,
    danger:
      `text-white font-bold bg-[#F33D62] border border-[#F33D62] ${
      !disabled ? "hover:border-transparent hover:bg-[#C00C31]" : ""
    } focus:border-transparent focus:bg-[#C00C31] disabled:bg-[#BBB] disabled:border-transparent`,
    outline:
      `text-[#FF861A] font-bold bg-white border border-[#FF861A] ${
      !disabled
        ? "hover:border-transparent hover:bg-[#FFEAD2] hover:text-[#B35400]"
        : ""
    } focus:border-[#FF861A] focus:bg-white focus:text-[#B35400] disabled:bg-white disabled:border-[#BBB] disabled:text-[#BBB]`,
    gray: `text-[#343A3D] font-bold bg-white border border-[#E0E0E0] ${
      !disabled
        ? "hover:border-transparent hover:bg-[#FFEAD2] hover:text-[#343A3D]"
        : ""
    } focus:border-[#FF861A] focus:bg-white focus:text-[#B35400] disabled:bg-white disabled:border-[#BBB] disabled:text-[#BBB]`,
    text: `h-[1.75rem] text-[14px] font-normal px-2 py-1 text-[#FF861A] ${
      !disabled ? "hover:bg-[#FFEAD2] hover:text-[#B35400] active:border active:border-[#FF861A]" : ""
    } alu-text-button-box-shadow active:border active:border-[#FF861A] focus:outline-none disabled:text-[#BBB] disabled:outline-none disabled:border-none`,
    danger_text: `h-[1.75rem] text-[14px] font-normal px-2 py-1 text-[#F33D62] ${
      !disabled ? "hover:bg-[#FDE9E9] hover:text-[#F33D62] active:border active:border-[#F33D62]" : ""
    } alu-text-button-box-shadow active:border active:border-[#F33D62] focus:outline-none disabled:text-[#BBB] disabled:outline-none disabled:border-none`,
  };

  const styleBasedErrorProp = !props.error
    ? "border alu-border active:border-[#FF861A] focus:border-[#FF861A]"
    : "border-2 border-[#F33D62]";

  const handleBlur = (event: any) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsOpen(false);
    }
  };

  return (
    <>
      {!isCombo ? 
        <button
          disabled={disabled}
          {...props}
          className={`${customStyle} rounded flex items-center justify-center gap-2 outline-none ${
            width === "full" ? "w-full" : "w-fit"
          } ${
            !noSizeVariants.includes(variant)
              ? `${styleBasedVariantProp[variant]} ${styleBasedSizeProp[size]}`
              : styleBasedVariantProp[variant]
          }`}
        >
          {props.children}
        </button>
        :
        <div className={`${width === "full" ? "w-full" : "w-fit"} relative`} onBlur={handleBlur} tabIndex={-1}>
          <button
            disabled={disabled}
            {...props}
            onClick={() => setIsOpen(prev => !prev)}
            className={`${customStyle} rounded flex items-center justify-center  ${
              variant !== "text"
                ? `${styleBasedVariantProp[variant]} ${styleComboBasedSizeProp[size]}`
                : styleBasedVariantProp[variant]
            }`}
          >
            {props.children}
            <span className={`w-[1px] h-full bg-current ${size ==="sm" ? "ml-[0.625rem]" : "ml-4"}`}></span>
            <svg className="ml-1" xmlns="http://www.w3.org/2000/svg" width={size === "md" ? "24" : "20"} height={size === "md" ? "24" : "20"} viewBox="0 0 24 24" fill="currentColor">
              <path d="M16.59 8.58997L12 13.17L7.41 8.58997L6 9.99997L12 16L18 9.99997L16.59 8.58997Z" fill="currentColor"/>
            </svg>
          </button>
          {isOpen && <div className="absolute top-[calc(100%+4px)] left-0 right-0 z-[9999] bg-white rounded py-2 flex flex-col gap-2 alu-combo-button-box-shadow min-w-max">
            {comboButtons && comboButtons?.map((button, index) => (
              <button tabIndex={0} onClick={() => {button.onClick(); setIsOpen(false);}} className="w-full text-sm text-left pl-8 p-2 hover:bg-[#FFF3E5] alu-text-base-2 disabled:bg-white disabled:text-[#BBB]" key={index}>
                {button.name}
              </button>
            ))}
          </div>}
        </div>
      }
    </>
  );
};

export default CustomButton;
